import Alert from '@common/components/Alert/Alert';
import { LoadingSpinner, SpinnerWrapper } from '@common/components/LoadingSpinner/LoadingSpinner';
import { BookableList, BookableListItemType } from '@features/List';
import { useGetPackagesAvailability } from '@features/packages/hooks/useGetPackagesAvailability';
import { getBookingRoute } from '@features/rates/helpers/bookingRoutes.helper';
import { Rate } from '@features/rates/types';
import { useTranslations } from '@lang';
import { ParsedJourneyParams } from '@store/yourJourney/selectors';
import { parseDateToString } from '@utils/helpers';
import moment from 'moment';
import { FC, useEffect } from 'react';
import { useRouter } from 'shared-routing';
import { Property } from 'shared-types/Property';
import { RateType } from 'shared-types/Rate';
import { ContentWrapper, LoadingOverlay } from './Package.styles';

type RoomsListingProps = {
  rate: Rate & RateType;
  journeyParams: ParsedJourneyParams;
  isFetching?: boolean;
  allUpgradeRoomsInBasket: boolean;
  property: Property;
  setIsLoadingAvailability: (isLoading: boolean) => void;
  setHasCompletedFirstFetch: (completed: boolean) => void;
  hasCompletedFirstFetch: boolean;
  packageDurationNights?: number;
};

export const RoomsListing: FC<RoomsListingProps> = ({
  rate,
  journeyParams,
  isFetching = false,
  property,
  setIsLoadingAvailability,
  setHasCompletedFirstFetch,
  hasCompletedFirstFetch,
}) => {
  const { query, pathname } = useRouter();
  const { t } = useTranslations();

  const { data: availability, isFetching: isFetchingAvailability } = useGetPackagesAvailability(
    property,
    [rate],
    journeyParams
  );
  const numberOfNights = moment(journeyParams.toDate).diff(moment(journeyParams.fromDate), 'days');

  useEffect(() => {
    if (!isFetchingAvailability && !hasCompletedFirstFetch) {
      setHasCompletedFirstFetch(true);
      return;
    }
    setIsLoadingAvailability(isFetchingAvailability);
  }, [isFetchingAvailability, hasCompletedFirstFetch, setIsLoadingAvailability]);

  if (isFetching) {
    return (
      <SpinnerWrapper>
        <LoadingSpinner size="50px" />
      </SpinnerWrapper>
    );
  }

  const loadingState = isFetchingAvailability || isFetching;
  const { rooms: _, ...journeyParamsNoRooms } = journeyParams;

  const isUnavailable = availability?.[0]?.isUnavailable ?? false;

  const defaultLinkCTA = t('room.bookRoom');
  const notAvailableTitle = t('rate.noRoomHeader');
  const notAvailableContent = t('rate.noRoomsInfo');

  const { rooms = [] } = rate;

  const items: BookableListItemType[] = rooms.map(
    ({ id, imageUrl, name: title, description, pricePerStay, pricePerNight, roomUpgrade }) => ({
      id,
      imageUrl,
      title,
      description,
      pricePerStay,
      pricePerNight,
      terms: '',
      numberOfNights,
      linkRoute: getBookingRoute(pathname),
      linkParams: {
        id: rate.id,
        room: id,
        ...journeyParamsNoRooms,
        promoRateCode: typeof query.promoRateCode === 'string' ? query.promoRateCode : undefined,
        fromDate: parseDateToString(journeyParams.fromDate),
        toDate: parseDateToString(journeyParams.toDate),
        spaDate: parseDateToString(journeyParams.fromDate),
        bookingStep: 'guestDetailsStep',
      },
      linkCTA: defaultLinkCTA,
      upgrade: roomUpgrade,
    })
  );

  return (
    <ContentWrapper isLoading={isFetching}>
      {items.length && !isUnavailable ? (
        <BookableList
          items={items}
          numberOfRooms={journeyParams?.rooms?.length || 1}
          isFetching={loadingState}
        />
      ) : (
        <Alert title={notAvailableTitle} content={notAvailableContent} />
      )}
      {loadingState && <LoadingOverlay />}
    </ContentWrapper>
  );
};
